import React, { useState } from "react";
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Switch,
  Box,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { ScoreCardRange } from "./ScorecardRange";

export interface CriteriaData {
  criterion: string;
  actual?: string;
  status?: string;
  tolerance?: number; // Tolerance value extracted from /+X format
  planA?: { value: string; status: string };
  planB?: { value: string; status: string };
}

export interface ScorecardStructure {
  structure: string;
  isTarget?: boolean;
  criteria: CriteriaData[];
}

interface ScorecardProps {
  mode: "single" | "comparison";
  data: ScorecardStructure[];
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
}

const Scorecard: React.FC<ScorecardProps> = ({
  mode,
  data,
  isLoading = false,
  title = "Scorecard",
  subtitle = "Dosimetric criteria displaying plan actuals against goals",
}) => {
  const [showBoxPlot, setShowBoxPlot] = useState(false);

  const theme = useTheme();
  const parseValue = (valueStr: string) => {
    // Extract numeric value from strings like "95.8%", "187cc"
    const match = valueStr.match(/^(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : 0;
  };

  const parseCriterion = (criterionStr: string) => {
    // Extract numeric value and operator from strings like "D98% ≥ 95%", "V70Gy ≤ 20%"
    const match = criterionStr.match(/([≥≤<>]+)\s*(\d+\.?\d*)/);
    if (match) {
      return {
        operator: match[1],
        value: parseFloat(match[2]),
      };
    }
    return { operator: "=", value: 0 };
  };

  const getStatus = (actualStr: string, criterionStr: string) => {
    const actualValue = parseValue(actualStr);
    const criterion = parseCriterion(criterionStr);

    switch (criterion.operator) {
      case "≥":
        return actualValue >= criterion.value ? "success" : "warning";
      case "≤":
        return actualValue <= criterion.value ? "success" : "warning";
      case ">":
        return actualValue > criterion.value ? "success" : "warning";
      case "<":
        return actualValue < criterion.value ? "success" : "warning";
      default:
        return "success";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />;
      case "warning":
        return <ErrorIcon sx={{ color: "warning.main", fontSize: 20 }} />;
      case "error":
        return <ErrorIcon sx={{ color: "error.main", fontSize: 20 }} />;
      default:
        return null;
    }
  };

  const renderComparisonTable = () => (
    <TableContainer
      sx={{
        backgroundColor: theme.palette.background.default,
        paddingTop: 2,
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow
            sx={{ th: { fontWeight: theme.typography.fontWeightBold } }}
          >
            <TableCell width="18%">Structure</TableCell>
            <TableCell width="22%">Dosimetric Criterion</TableCell>
            <TableCell width="15%">Plan A</TableCell>
            <TableCell width="15%">Plan B</TableCell>
            <TableCell width="30%">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="inherit">Scorecard Range</Typography>
                  <Tooltip
                    title="Visual comparison showing how Plan A and Plan B values perform against community data ranges. Toggle 'Community' to show/hide the reference box plots that represent typical performance ranges from similar cases."
                    arrow
                    placement="top"
                  >
                    <InfoIcon
                      sx={{
                        fontSize: 16,
                        color: "text.secondary",
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography
                    variant="inherit"
                    sx={{
                      fontSize: "0.75em",
                      color: "#666",
                      fontWeight: "normal",
                    }}
                  >
                    Community
                  </Typography>
                  <Switch
                    checked={showBoxPlot}
                    onChange={(e) => setShowBoxPlot(e.target.checked)}
                    size="small"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#745de5",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#745de5",
                        },
                    }}
                  />
                </Box>
              </Box>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((structureData, structureIndex) => (
            <React.Fragment key={structureIndex}>
              {structureData.criteria.map((criteriaData, criteriaIndex) => (
                <TableRow
                  key={`${structureData.structure}_${criteriaIndex}`}
                  sx={{
                    height: 70,
                    position: "relative",
                    td: {
                      border: 0,
                    },
                    ...(criteriaIndex === structureData.criteria.length - 1 &&
                      structureIndex !== data.length - 1 && {
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: 0,
                          left: "10px",
                          right: "10px",
                          height: "1px",
                          backgroundColor: theme.palette.divider,
                        },
                      }),
                  }}
                >
                  <TableCell width="18%">
                    <Typography variant="body2">
                      {criteriaIndex === 0 ? structureData.structure : ""}
                    </Typography>
                  </TableCell>
                  <TableCell width="22%">
                    <Typography variant="body2">
                      {criteriaData.criterion}
                    </Typography>
                  </TableCell>
                  <TableCell width="15%">
                    <Stack direction="row" alignItems="center" gap={1}>
                      {getStatusIcon(criteriaData.planA?.status || "success")}
                      <Typography variant="body2">
                        {criteriaData.planA?.value}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell width="15%">
                    <Stack direction="row" alignItems="center" gap={1}>
                      {getStatusIcon(criteriaData.planB?.status || "success")}
                      <Typography variant="body2">
                        {criteriaData.planB?.value}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell
                    width="30%"
                    sx={{ textAlign: "left", paddingLeft: 1 }}
                  >
                    <ScoreCardRange
                      planAValue={parseFloat(criteriaData.planA?.value || "0")}
                      planBValue={parseFloat(criteriaData.planB?.value || "0")}
                      criterion={criteriaData.criterion}
                      tolerance={criteriaData.tolerance}
                      showBoxPlot={showBoxPlot}
                      singlePlanMode={false}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (isLoading) {
    return (
      <Card variant="outlined">
        <CardHeader
          title={<Typography variant="h6">{title}</Typography>}
          subheader={subtitle}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">Loading scorecard...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card variant="outlined">
        <CardHeader
          title={<Typography variant="h6">{title}</Typography>}
          subheader={subtitle}
        />
        <Divider />
        <CardContent>
          <Typography variant="body1">No scorecard data available.</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ maxHeight: "calc(100vh - 480px)" }}>
      <CardHeader
        title={<Typography variant="h6">{title}</Typography>}
        subheader={subtitle}
      />
      <Divider />
      <CardContent sx={{ overflow: "auto", maxHeight: "57vh" }}>
        {renderComparisonTable()}
      </CardContent>
    </Card>
  );
};

export default Scorecard;
